import React, { useCallback, useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftSnackbar from "components/SoftSnackbar";
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { API_URL } from "config";
import { useAuth } from "../../../AuthContext";
import { hasPermission, mergeGrantedPermissions } from "utils/granularPermissions";
import { apiErrorMessage, apiSuccessMessage, readJsonSafely } from "utils/apiResponseMessages";
import { shiftDisplayLabel } from "utils/shiftDisplay";

const weekendOffOptions = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const normalizeWeekendOffValue = (value) => {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value.filter((day) => weekendOffOptions.includes(day));
  }

  if (typeof value === "string") {
    try {
      const decoded = JSON.parse(value);
      if (Array.isArray(decoded)) {
        return decoded.filter((day) => weekendOffOptions.includes(day));
      }
    } catch (error) {
      // Fall back to comma/pipe-separated legacy strings.
    }

    return value
      .split(/[,|]/)
      .map((day) => day.trim())
      .filter((day) => weekendOffOptions.includes(day));
  }

  return [];
};

const normalizeLeaderOption = (leader) => {
  if (!leader?.id) return null;

  return {
    ...leader,
    id: String(leader.id),
    name: leader.name || "Current Reporting User",
  };
};

const mergeLeaderOptions = (options, currentLeader) => {
  const rows = (options || []).map(normalizeLeaderOption).filter(Boolean);
  const current = normalizeLeaderOption(currentLeader);

  if (current && !rows.some((leader) => leader.id === current.id)) {
    rows.push(current);
  }

  return rows;
};

const EditUser = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthReady, permissions = [], userPermission = [] } = useAuth();

  const [formData, setFormData] = useState({});
  const [branches, setBranches] = useState([]);
  const [subBranches, setSubBranches] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [teamLeaders, setTeamLeaders] = useState([]);
  const [roles, setRoles] = useState([]);
  const [attendanceSettingsEditable, setAttendanceSettingsEditable] = useState(false);
  const [loading, setLoading] = useState({
    initial: true,
    form: false,
    shifts: false,
    teamLeaders: false,
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", color: "success" });
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyExporting, setHistoryExporting] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historyRows, setHistoryRows] = useState([]);
  const [historyPagination, setHistoryPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
    from: 0,
    to: 0,
  });

  const headerCellSx = {
    backgroundColor: "#f6f8fb",
    color: "#64748b",
    fontSize: "13px",
    fontWeight: 600,
    borderBottom: "1px solid #e2e8f0",
    py: 1.4,
    whiteSpace: "nowrap",
  };

  const bodyCellSx = {
    color: "#334155",
    fontSize: "14px",
    borderBottom: "1px solid #edf2f7",
    py: 1.3,
    verticalAlign: "middle",
  };

  const grantedPermissions = useMemo(
    () => mergeGrantedPermissions(permissions, userPermission),
    [permissions, userPermission]
  );
  const hasUsersPermission = useCallback(
    (...requiredPermissions) => hasPermission(grantedPermissions, ...requiredPermissions),
    [grantedPermissions]
  );
  const canViewPage = hasUsersPermission("users.pages.edit.view");
  const canSubmit =
    hasUsersPermission("users.actions.update") &&
    hasUsersPermission("users.actions.submit_update");
  const canOpenList =
    hasUsersPermission("users.pages.list.view") &&
    hasUsersPermission("users.actions.open_list");
  const canViewField = useCallback(
    (field) => hasUsersPermission(`users.fields.${field}.view`),
    [hasUsersPermission]
  );

  const selectedRole = useMemo(
    () => roles.find((role) => String(role.id) === String(formData.role_id || "")),
    [roles, formData.role_id]
  );
  const hasParentRole = Boolean(selectedRole?.parent_role_id);
  const requiresReportingUser =
    hasParentRole && selectedRole?.requires_reporting_user !== false;

  const fetchWithAuth = useCallback(async (url, options = {}) => {
    const token = localStorage.getItem("authToken");
    const response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
    if (!response.ok) {
      const errorData = await readJsonSafely(response);
      throw new Error(apiErrorMessage(errorData, `Request failed (${response.status})`));
    }
    return readJsonSafely(response);
  }, []);

  const formatDateTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  const humanizeKey = (key) => String(key || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

  const formatHistoryValue = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    if (Array.isArray(value)) return value.join(", ");
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  };

  const formatHistoryChanges = (oldValues = {}, newValues = {}) => {
    const keys = Array.from(new Set([
      ...Object.keys(oldValues || {}),
      ...Object.keys(newValues || {}),
    ]));

    if (keys.length === 0) return "-";

    return keys.map((key) => `${humanizeKey(key)}: ${formatHistoryValue(oldValues?.[key])} -> ${formatHistoryValue(newValues?.[key])}`).join("; ");
  };

  const fetchHistory = async (page = 1) => {
    setHistoryLoading(true);
    setHistoryError("");

    try {
      const data = await fetchWithAuth(`${API_URL}/users/${id}/history?page=${page}&per_page=${historyPagination.per_page}`);
      const logs = data.logs || {};
      setHistoryRows(Array.isArray(logs.data) ? logs.data : []);
      setHistoryPagination({
        current_page: logs.current_page || page,
        last_page: logs.last_page || 1,
        per_page: logs.per_page || 10,
        total: logs.total || 0,
        from: logs.from || 0,
        to: logs.to || 0,
      });
    } catch (error) {
      setHistoryError(error.message || "Failed to load history");
    } finally {
      setHistoryLoading(false);
    }
  };

  const openHistory = () => {
    setHistoryOpen(true);
    fetchHistory(1);
  };

  const exportHistory = async () => {
    setHistoryExporting(true);
    setHistoryError("");

    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_URL}/users/${id}/history/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `user-history-${id}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setHistoryError(error.message || "Failed to export history");
    } finally {
      setHistoryExporting(false);
    }
  };

  useEffect(() => {
    if (!isAuthReady) return;

    if (!canViewPage) {
      setLoading(prev => ({ ...prev, initial: false }));
      return;
    }

    const fetchUserData = async () => {
      try {
        setLoading(prev => ({ ...prev, initial: true }));
        const res = await fetchWithAuth(`${API_URL}/users/${id}`);
        const user = res.user;

        // Format shifts for dropdown
        const formattedShifts = (res.dropdowns.shifts || []).map(shift => ({
          ...shift,
          label: shiftDisplayLabel(shift),
        }));

        // Filter out admin role
        const filteredRoles = res.dropdowns.roles || [];

        // Set all data
        setBranches(res.dropdowns.branches || []);
        setShifts(formattedShifts);
        setTeamLeaders(mergeLeaderOptions(res.dropdowns.team_leaders || [], user.team_leader));
        setRoles(filteredRoles);
        setAttendanceSettingsEditable(Boolean(user.attendance_settings_editable));

        const userShifts = user.shifts || [];
        const primaryShiftId = userShifts[0]?.id?.toString() || "";

        // Set form data with current user values
        setFormData({
          name: user.name || "",
          email: user.email || "",
          branch_id: user.branch_id?.toString() || "",
          subbranch_id: user.subbranch_id?.toString() || "",
          shift_id: primaryShiftId,
          team_leader_id: user.team_leader_id?.toString() || "",
          team_leader_name: user.team_leader?.name || "",
          role_id: user.roles?.[0]?.id?.toString() || "",
          user_code: user.user_code || "",
          weekend_off: normalizeWeekendOffValue(user.weekend_off || user.weekendOff || []),
          status: user.status || "",
          joining_date: user.joining_date
            ? user.joining_date.slice(0, 10)
            : "",

          appointment_date: user.appointment_date
            ? user.appointment_date.slice(0, 10)
            : "",
          flexible_hours_allowed: Boolean(user.attendance_settings?.flexible_hours_allowed),
          vendor_projects_visible: Boolean(user.attendance_settings?.vendor_projects_visible),

          password: "",
          confirmPassword: "",
        });

      } catch (error) {
        setSnackbar({ open: true, message: error.message, color: "error" });
      } finally {
        setLoading(prev => ({ ...prev, initial: false }));
      }
    };
    fetchUserData();
  }, [canViewPage, fetchWithAuth, id, isAuthReady]);

  useEffect(() => {
    const loadBranchData = async () => {
      if (!formData.branch_id) {
        setSubBranches([]);
        return;
      }

      try {
        const subBranchesData = canViewField("sub_branch")
          ? await fetchWithAuth(`${API_URL}/users/options/sub-branches/${formData.branch_id}`)
          : { data: [] };

        const formattedSubBranches = subBranchesData.data || subBranchesData.subbranches || subBranchesData || [];
        setSubBranches(formattedSubBranches);

        // Ensure subbranch stays valid or resets
        if (formData.subbranch_id) {
           const exists = formattedSubBranches.find(sb => sb.id.toString() === formData.subbranch_id);
           if (!exists) setFormData(prev => ({ ...prev, subbranch_id: "" }));
        }

      } catch (error) {
        setSnackbar({ open: true, message: error.message, color: "error" });
      }
    };
    loadBranchData();
  }, [canViewField, fetchWithAuth, formData.branch_id, formData.subbranch_id]);

  useEffect(() => {
    const loadShifts = async () => {
      if (!formData.subbranch_id) {
        setShifts([]);
        return;
      }
      try {
        setLoading(prev => ({ ...prev, shifts: true }));
        const shiftsData = await fetchWithAuth(`${API_URL}/users/options/shifts/${formData.subbranch_id}`);
        
        const rawShifts = shiftsData.data || shiftsData.shifts || shiftsData || [];
        const formattedShifts = rawShifts.map(shift => ({
          ...shift,
          label: shiftDisplayLabel(shift),
        }));

        setShifts(formattedShifts);

        const shiftExists = formattedShifts.some(s => s.id.toString() === formData.shift_id);
        if (!shiftExists) {
          setFormData(prev => ({ ...prev, shift_id: "" }));
        }
      } catch (error) {
        setSnackbar({ open: true, message: error.message, color: "error" });
      } finally {
        setLoading(prev => ({ ...prev, shifts: false }));
      }
    };
    loadShifts();
  }, [fetchWithAuth, formData.subbranch_id]);

  useEffect(() => {
    const loadTeamLeaders = async () => {
      const currentLeader = formData.team_leader_id
        ? {
            id: String(formData.team_leader_id),
            name: formData.team_leader_name || "Current Reporting User",
          }
        : null;

      if (
        !canViewField("team_leader") ||
        !selectedRole?.id ||
        (!requiresReportingUser && !currentLeader) ||
        !formData.branch_id ||
        !formData.subbranch_id ||
        !formData.shift_id
      ) {
        setTeamLeaders(currentLeader ? [currentLeader] : []);
        return;
      }

      try {
        setLoading(prev => ({ ...prev, teamLeaders: true }));

        const reportingParams = new URLSearchParams({
          role_id: String(selectedRole.id),
          exclude_user_id: String(id),
          ...(formData.branch_id ? { branch_id: String(formData.branch_id) } : {}),
          ...(formData.subbranch_id ? { subbranch_id: String(formData.subbranch_id) } : {}),
          ...(formData.shift_id ? { shift_id: String(formData.shift_id) } : {}),
        });

        const data = await fetchWithAuth(
          `${API_URL}/users/options/team-leaders?${reportingParams.toString()}`
        );

        const rawLeaderData = data.data || data.team_leaders || data || [];
        const formatted = mergeLeaderOptions(rawLeaderData, currentLeader);

        setTeamLeaders(formatted);

      } catch (error) {
        setSnackbar({ open: true, message: error.message, color: "error" });
      } finally {
        setLoading(prev => ({ ...prev, teamLeaders: false }));
      }
    };

    loadTeamLeaders();
  }, [
    canViewField,
    fetchWithAuth,
    formData.branch_id,
    formData.team_leader_id,
    formData.team_leader_name,
    formData.shift_id,
    formData.subbranch_id,
    id,
    requiresReportingUser,
    selectedRole?.id,
  ]);


  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => {
      const newData = { ...prev, [name]: value };

      if (name === "role_id") {
        // Reset shift selections when role changes
        newData.shift_id = prev.shift_id;
        newData.team_leader_id = "";
        newData.team_leader_name = "";
      } else if (name === "branch_id") {
        newData.subbranch_id = "";
        newData.shift_id = "";
        newData.team_leader_id = "";
        newData.team_leader_name = "";
      } else if (name === "subbranch_id") {
        newData.shift_id = "";
        newData.team_leader_id = "";
        newData.team_leader_name = "";
      } else if (name === "shift_id") {
        newData.team_leader_id = "";
        newData.team_leader_name = "";
      } else if (name === "team_leader_id") {
        const selectedLeader = teamLeaders.find((leader) => String(leader.id) === String(value));
        newData.team_leader_name = selectedLeader?.name || "";
      }

      return newData;
    });
  };

  const handleWeekendOffToggle = (day) => {
    setFormData((prev) => {
      const selectedDays = normalizeWeekendOffValue(prev.weekend_off);
      const nextDays = selectedDays.includes(day)
        ? selectedDays.filter((selectedDay) => selectedDay !== day)
        : [...selectedDays, day];

      return { ...prev, weekend_off: nextDays };
    });
  };

  const toYMD = (date) => {
    if (!date) return null;
    return new Date(date).toISOString().slice(0, 10);
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!canSubmit) {
      setSnackbar({ open: true, message: "You do not have permission to update users", color: "error" });
      return;
    }

    setLoading(prev => ({ ...prev, form: true }));

    if (formData.password && formData.password !== formData.confirmPassword) {
      setSnackbar({ open: true, message: "Passwords do not match", color: "error" });
      setLoading(prev => ({ ...prev, form: false }));
      return;
    }

    // 🔒 Status based validation (same as Add User)
    if (!formData.status) {
      setSnackbar({ open: true, message: "Status is required", color: "error" });
      setLoading(prev => ({ ...prev, form: false }));
      return;
    }

    if (formData.status === "training" && !formData.joining_date) {
      setSnackbar({
        open: true,
        message: "Joining Date is required for Training status",
        color: "error",
      });
      setLoading(prev => ({ ...prev, form: false }));
      return;
    }

    if (
      formData.status === "selected" &&
      (!formData.joining_date || !formData.appointment_date)
    ) {
      setSnackbar({
        open: true,
        message: "Joining Date and Appointment Date are required for Selected status",
        color: "error",
      });
      setLoading(prev => ({ ...prev, form: false }));
      return;
    }


    try {
      const payload = {

        name: formData.name,
        email: formData.email,
        branch_id: parseInt(formData.branch_id),
        subbranch_id: formData.subbranch_id ? parseInt(formData.subbranch_id) : null,
        team_leader_id: formData.team_leader_id ? parseInt(formData.team_leader_id) : null,
        roles: [parseInt(formData.role_id)],
        user_code: formData.user_code,
        weekend_off: normalizeWeekendOffValue(formData.weekend_off),
        status: formData.status,
        joining_date: toYMD(formData.joining_date),
        appointment_date: toYMD(formData.appointment_date),
      };

      if (!formData.shift_id) {
        throw new Error("Please select a shift");
      }
      payload.shift_id = parseInt(formData.shift_id);

      if (formData.password) {
        payload.password = formData.password;
        payload.password_confirmation = formData.confirmPassword;
      }

      if (attendanceSettingsEditable) {
        payload.attendance_settings = {
          flexible_hours_allowed: Boolean(formData.flexible_hours_allowed),
          vendor_projects_visible: Boolean(formData.vendor_projects_visible),
        };
      }


      const data = await fetchWithAuth(`${API_URL}/users/${id}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });

      setSnackbar({
        open: true,
        message: apiSuccessMessage(data, "User updated successfully"),
        color: "success",
      });
      setTimeout(() => navigate("/users/list"), 1500);
    } catch (error) {
      setSnackbar({ open: true, message: error.message, color: "error" });
    } finally {
      setLoading(prev => ({ ...prev, form: false }));
    }
  };

  // Field disabled states
  const isBranchDisabled = !formData.role_id;
  const isSubBranchDisabled = !formData.branch_id;
  const isShiftDisabled = !formData.subbranch_id || loading.shifts;
  const needsReportingUser = requiresReportingUser || Boolean(formData.team_leader_id);
  const isTeamLeaderDisabled =
    !needsReportingUser ||
    !formData.branch_id ||
    !formData.subbranch_id ||
    !formData.shift_id ||
    loading.teamLeaders;

  if (!isAuthReady || loading.initial) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox p={3} display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <CircularProgress />
        </SoftBox>
      </DashboardLayout>
    );
  }

  if (!canViewPage) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox p={3}>
          <SoftTypography variant="h5" fontWeight="medium">
            Edit User
          </SoftTypography>
          <SoftTypography variant="body2" color="text" mt={1}>
            You do not have permission to edit users.
          </SoftTypography>
        </SoftBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftSnackbar
        color={snackbar.color}
        icon="notifications"
        title="User"
        content={snackbar.message}
        open={snackbar.open}
        close={() => setSnackbar({ ...snackbar, open: false })}
        time={3000}
      />
      <SoftBox p={3}>
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <SoftTypography variant="h5" fontWeight="medium">
            Edit User
          </SoftTypography>
          <SoftButton size="small" color="dark" variant="outlined" onClick={openHistory}>
            History
          </SoftButton>
        </SoftBox>
        <SoftBox component="form" onSubmit={handleSubmit} autoComplete="off" noValidate>
          {/* Name */}
          {canViewField("name") && (
          <SoftBox mb={2}>
            <SoftTypography variant="caption" fontWeight="bold">Name</SoftTypography>
            <SoftInput
              type="text"
              name="name"
              value={formData.name || ""}
              onChange={handleChange}
              required
              fullWidth
              autoComplete="off"
            />
          </SoftBox>
          )}

          {/* Email */}
          {canViewField("email") && (
          <SoftBox mb={2}>
            <SoftTypography variant="caption" fontWeight="bold">Email</SoftTypography>
            <SoftInput
              type="email"
              name="email"
              value={formData.email || ""}
              onChange={handleChange}
              required
              fullWidth
              autoComplete="off"
            />
          </SoftBox>
          )}

          {/* Role */}
          {canViewField("role") && (
          <SoftBox mb={2}>
            <SoftTypography variant="caption" fontWeight="bold">Role</SoftTypography>
            <select
              name="role_id"
              value={formData.role_id || ""}
              onChange={handleChange}
              required
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "6px",
                border: "1px solid #d2d6da",
                fontSize: "14px",
              }}
            >
              <option value="">Select Role</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
          </SoftBox>
          )}

          {/* Branch */}
          {canViewField("branch") && (
          <SoftBox mb={2}>
            <SoftTypography variant="caption" fontWeight="bold">Branch</SoftTypography>
            <select
              name="branch_id"
              value={formData.branch_id || ""}
              onChange={handleChange}
              required
              disabled={isBranchDisabled}
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "6px",
                border: "1px solid #d2d6da",
                fontSize: "14px",
                backgroundColor: isBranchDisabled ? "#f5f5f5" : "white",
              }}
            >
              <option value="">Select Branch</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </select>
          </SoftBox>
          )}

          {/* Sub Branch */}
          {canViewField("sub_branch") && (
          <SoftBox mb={2}>
            <SoftTypography variant="caption" fontWeight="bold">Sub Branch</SoftTypography>
            <select
              name="subbranch_id"
              value={formData.subbranch_id || ""}
              onChange={handleChange}
              required
              disabled={isSubBranchDisabled}
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "6px",
                border: "1px solid #d2d6da",
                fontSize: "14px",
                backgroundColor: isSubBranchDisabled ? "#f5f5f5" : "white",
              }}
            >
              <option value="">Select Sub Branch</option>
              {subBranches.map((sb) => (
                <option key={sb.id} value={sb.id}>
                  {sb.name}
                </option>
              ))}
            </select>
          </SoftBox>
          )}

          {/* Shift */}
          {canViewField("shift") && selectedRole && (
            <SoftBox mb={2}>
              <SoftTypography variant="caption" fontWeight="bold">Shift</SoftTypography>
              <select
                name="shift_id"
                value={formData.shift_id || ""}
                onChange={handleChange}
                required
                disabled={isShiftDisabled}
                style={{
                  width: "100%",
                  padding: "8px",
                  borderRadius: "6px",
                  border: "1px solid #d2d6da",
                  fontSize: "14px",
                  backgroundColor: isShiftDisabled ? "#f5f5f5" : "white",
                }}
              >
                <option value="">Select Shift</option>
                {shifts.map((shift) => (
                  <option key={shift.id} value={shift.id}>
                    {shift.label}
                  </option>
                ))}
              </select>
            </SoftBox>
          )}

          {attendanceSettingsEditable && (
            <SoftBox mb={2} display="flex" gap={2} flexWrap="wrap">
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(formData.flexible_hours_allowed)}
                    onChange={(event) =>
                      setFormData((previous) => ({
                        ...previous,
                        flexible_hours_allowed: event.target.checked,
                      }))
                    }
                  />
                }
                label="Flexible hours"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(formData.vendor_projects_visible)}
                    onChange={(event) =>
                      setFormData((previous) => ({
                        ...previous,
                        vendor_projects_visible: event.target.checked,
                      }))
                    }
                  />
                }
                label="Vendor Projects always visible"
              />
            </SoftBox>
          )}

          {/* Reporting User */}
          {canViewField("team_leader") && needsReportingUser && (
            <SoftBox mb={2}>
              <SoftTypography variant="caption" fontWeight="bold">
                Team Leader / Reporting User
              </SoftTypography>
              <select
                name="team_leader_id"
                value={formData.team_leader_id || ""}
                onChange={handleChange}
                required={needsReportingUser}
                disabled={isTeamLeaderDisabled}
                style={{
                  width: "100%",
                  padding: "8px",
                  borderRadius: "6px",
                  border: "1px solid #d2d6da",
                  fontSize: "14px",
                  backgroundColor: isTeamLeaderDisabled ? "#f5f5f5" : "white",
                }}
              >
                <option value="">Select Reporting User</option>
                {teamLeaders.map((leader) => (
                  <option key={leader.id} value={leader.id}>
                    {leader.name}
                  </option>
                ))}
              </select>
            </SoftBox>
          )}

          {/* Status */}
          {canViewField("status") && (
          <SoftBox mb={2}>
            <SoftTypography variant="caption" fontWeight="bold">
              Status
            </SoftTypography>
            <select
              name="status"
              value={formData.status || ""}
              onChange={handleChange}
              required
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "6px",
                border: "1px solid #d2d6da",
                fontSize: "14px",
              }}
            >
              <option value="">Select Status</option>
              <option value="training">Training</option>
              <option value="selected">Selected</option>
            </select>
          </SoftBox>
          )}

          {(canViewField("joining_date") || canViewField("appointment_date")) &&
            (formData.status === "training" || formData.status === "selected") && (
            <SoftBox mb={2} display="flex" gap={2} flexWrap="wrap">
              {/* Joining Date */}
              {canViewField("joining_date") && (
              <SoftBox sx={{ width: { xs: "100%", sm: "48%" } }}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Joining Date
                </SoftTypography>
                <SoftInput
                  type="date"
                  name="joining_date"
                  value={formData.joining_date || ""}
                  onChange={handleChange}
                  required
                  fullWidth
                />
              </SoftBox>
              )}

              {/* Appointment Date */}
              {canViewField("appointment_date") && (
              <SoftBox sx={{ width: { xs: "100%", sm: "48%" } }}>
                <SoftTypography variant="caption" fontWeight="bold">
                  Appointment Date
                </SoftTypography>
                <SoftInput
                  type="date"
                  name="appointment_date"
                  value={formData.appointment_date || ""}
                  onChange={handleChange}
                  required={formData.status === "selected"}
                  fullWidth
                />
              </SoftBox>
              )}
            </SoftBox>
          )}

          {/* Employee Code */}
          {canViewField("user_code") && (
          <SoftBox mb={2}>
            <SoftTypography variant="caption" fontWeight="bold">
              Employee Code
            </SoftTypography>
            <SoftInput
              type="text"
              name="user_code"
              value={formData.user_code || ""}
              onChange={handleChange}
              required
              fullWidth
              autoComplete="off"
            />
          </SoftBox>
          )}

          {/* Weekend Off */}
          {canViewField("weekend_off") && (
          <SoftBox mb={2}>
            <SoftTypography variant="caption" fontWeight="bold">
              Weekend Off
            </SoftTypography>
            <SoftBox
              display="flex"
              flexWrap="wrap"
              gap={1.25}
              mt={0.75}
              style={{
                padding: "8px 10px",
                border: "1px solid #d2d6da",
                borderRadius: "6px",
              }}
            >
              {weekendOffOptions.map((day) => {
                const selectedDays = normalizeWeekendOffValue(formData.weekend_off);

                return (
                  <label
                    key={day}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      fontSize: "14px",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedDays.includes(day)}
                      onChange={() => handleWeekendOffToggle(day)}
                    />
                    {day}
                  </label>
                );
              })}
            </SoftBox>
          </SoftBox>
          )}



          {/* Password */}
          {canViewField("password") && (
          <SoftBox mb={2}>
            <SoftTypography variant="caption" fontWeight="bold">New Password (Optional)</SoftTypography>
            <SoftInput
              type="password"
              name="password"
              value={formData.password || ""}
              onChange={handleChange}
              fullWidth
              autoComplete="new-password"
            />
          </SoftBox>
          )}

          {/* Confirm Password */}
          {canViewField("password_confirmation") && (
          <SoftBox mb={2}>
            <SoftTypography variant="caption" fontWeight="bold">Confirm Password</SoftTypography>
            <SoftInput
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword || ""}
              onChange={handleChange}
              fullWidth
              autoComplete="new-password"
            />
          </SoftBox>
          )}

          {/* Submit */}
          <SoftBox mt={3} display="flex" justifyContent="flex-end" gap={2}>
            {canOpenList && (
            <SoftButton color="secondary" onClick={() => navigate("/users/list")} disabled={loading.form}>
              Cancel
            </SoftButton>
            )}
            {canSubmit && (
            <SoftButton type="submit" color="dark" disabled={loading.form}>
              {loading.form ? "Processing..." : "Update User"}
            </SoftButton>
            )}
          </SoftBox>
        </SoftBox>
      </SoftBox>
      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <SoftBox display="flex" justifyContent="space-between" alignItems="center">
            <SoftTypography variant="h6" fontWeight="bold">History</SoftTypography>
            <SoftButton size="small" color="info" variant="gradient" onClick={exportHistory} disabled={historyExporting}>
              {historyExporting ? "Exporting..." : "Export Excel"}
            </SoftButton>
          </SoftBox>
        </DialogTitle>
        <DialogContent dividers>
          {historyError && (
            <SoftTypography color="error" variant="body2" mb={2}>{historyError}</SoftTypography>
          )}
          <TableContainer component={Paper} sx={{ boxShadow: "none", overflowX: "auto" }}>
            <Table sx={{ minWidth: 980 }}>
              <TableHead sx={{ display: "table-header-group" }}>
                <TableRow>
                  <TableCell sx={headerCellSx}>Date & Time</TableCell>
                  <TableCell sx={headerCellSx}>Action</TableCell>
                  <TableCell sx={headerCellSx}>Record</TableCell>
                  <TableCell sx={headerCellSx}>Actor</TableCell>
                  <TableCell sx={headerCellSx}>Changes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historyLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={bodyCellSx}>
                      <SoftBox display="flex" justifyContent="center" alignItems="center" minHeight={140}>
                        <CircularProgress size={28} />
                      </SoftBox>
                    </TableCell>
                  </TableRow>
                ) : historyRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={bodyCellSx}>
                      <SoftBox py={3} textAlign="center">
                        <SoftTypography variant="body2" color="text">No history found.</SoftTypography>
                      </SoftBox>
                    </TableCell>
                  </TableRow>
                ) : (
                  historyRows.map((log) => (
                    <TableRow key={log.id} hover sx={{ "&:hover": { backgroundColor: "#f8fafc" } }}>
                      <TableCell sx={bodyCellSx}>{formatDateTime(log.created_at)}</TableCell>
                      <TableCell sx={bodyCellSx}>{humanizeKey(log.action)}</TableCell>
                      <TableCell sx={bodyCellSx}>
                        <SoftBox display="flex" flexDirection="column">
                          <SoftTypography variant="body2" color="dark" sx={{ fontSize: "14px", fontWeight: 600 }}>
                            {log.subject_name || `#${log.subject_id}`}
                          </SoftTypography>
                          <SoftTypography variant="caption" color="text">{humanizeKey(log.subject_type)}</SoftTypography>
                        </SoftBox>
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <SoftBox display="flex" flexDirection="column">
                          <SoftTypography variant="body2" color="dark" sx={{ fontSize: "14px", fontWeight: 600 }}>
                            {log.actor_name || "-"}
                          </SoftTypography>
                          {log.actor_email && <SoftTypography variant="caption" color="text">{log.actor_email}</SoftTypography>}
                        </SoftBox>
                      </TableCell>
                      <TableCell sx={{ ...bodyCellSx, minWidth: 320 }}>
                        <SoftTypography variant="body2" sx={{ fontSize: "13px", lineHeight: 1.45, wordBreak: "break-word" }}>
                          {formatHistoryChanges(log.old_values, log.new_values)}
                        </SoftTypography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <SoftBox display="flex" justifyContent="space-between" alignItems="center" mt={2}>
            <SoftTypography variant="body2" color="text">
              Showing {historyPagination.from || 0} to {historyPagination.to || 0} of {historyPagination.total || 0} entries
            </SoftTypography>
            <SoftBox display="flex" gap={1}>
              <SoftButton size="small" color="dark" variant="outlined" disabled={historyLoading || historyPagination.current_page <= 1} onClick={() => fetchHistory(historyPagination.current_page - 1)}>
                Previous
              </SoftButton>
              <SoftButton size="small" color="dark" variant="outlined" disabled={historyLoading || historyPagination.current_page >= historyPagination.last_page} onClick={() => fetchHistory(historyPagination.current_page + 1)}>
                Next
              </SoftButton>
            </SoftBox>
          </SoftBox>
        </DialogContent>
        <DialogActions>
          <SoftButton color="dark" variant="outlined" onClick={() => setHistoryOpen(false)}>
            Close
          </SoftButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default EditUser;
